import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Store, Search as SearchIcon, Share2, MapPin, Star, TrendingUp, ShoppingBag, Gavel, Link as LinkIcon,
} from "lucide-react";
import { toast } from "sonner";
import { TOP_LEVEL_CATEGORIES } from "@/data/taxonomy";

const CATEGORIES = (TOP_LEVEL_CATEGORIES || []).map((label) => ({ key: label, label }));

const SHOPS_PAGE_SIZE = 30;

// Shops surface — every approved business rendered as a big, image-forward
// "shop card". Same source of truth as /directory but presented with an
// Etsy-esque grid and one-click social sharing.

export default function Shops() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [city, setCity] = useState(params.get("city") || "");
  const [sort, setSort] = useState(params.get("sort") || "popular");
  const [items, setItems] = useState([]);
  const [liveSessions, setLiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (category) p.set("category", category);
    if (city) p.set("city", city);
    if (sort !== "popular") p.set("sort", sort);
    setParams(p, { replace: true });
  }, [q, category, city, sort, setParams]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [shopsR, liveR] = await Promise.all([
          api.get("/businesses", {
            params: {
              q: q || undefined,
              category: category || undefined,
              city: city || undefined,
              sort,
              offset: 0,
              limit: SHOPS_PAGE_SIZE,
            },
          }),
          // Pull any live-selling sessions so we can render the top strip
          // AND flag the corresponding shop cards below.
          api.get("/marketplace/live/sessions", { params: { status: "live", limit: 12 } }).catch(() => ({ data: {} })),
        ]);
        if (!alive) return;
        setItems(shopsR.data?.items || shopsR.data || []);
        setLiveSessions(liveR.data?.items || []);
      } catch (e) {
        if (alive) toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally { if (alive) setLoading(false); }
    })();
  }, [q, category, city, sort]);

  // Fast lookup — for cards below, is this business currently live?
  // Maps host_id → viewer_count so we can also boost by audience size.
  const liveViewerCountByHost = useMemo(() => {
    const m = new Map();
    for (const s of liveSessions) {
      if (s.host_type !== "business") continue;
      const prev = m.get(s.host_id) || 0;
      m.set(s.host_id, Math.max(prev, s.viewer_count || 0));
    }
    return m;
  }, [liveSessions]);

  const liveHostIds = useMemo(() => new Set(liveViewerCountByHost.keys()), [liveViewerCountByHost]);

  // Boost live shops to the top of the feed, ordered by viewer count desc.
  // Everything else stays in its incoming server-sorted order (stable sort).
  const displayedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const aLive = liveHostIds.has(a.id);
      const bLive = liveHostIds.has(b.id);
      if (aLive && !bLive) return -1;
      if (!aLive && bLive) return 1;
      if (aLive && bLive) {
        return (liveViewerCountByHost.get(b.id) || 0) - (liveViewerCountByHost.get(a.id) || 0);
      }
      return 0; // preserve server order
    });
  }, [items, liveHostIds, liveViewerCountByHost]);

  // Only surface business-hosted live sessions in the strip
  const businessLive = useMemo(
    () => [...liveSessions]
      .filter((s) => s.host_type === "business")
      .sort((a, b) => (b.viewer_count || 0) - (a.viewer_count || 0)),
    [liveSessions]
  );

  return (
    <div className="min-h-[100vh]" data-testid="shops-page">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-[#EAE3DE] bg-gradient-to-br from-[#FDF3F1] via-white to-[#FAF5F2]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#F0C9C4] px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#B31E1E]">
                <Store size={12} /> Shops
              </div>
              <h1 className="mt-3 text-4xl md:text-6xl font-black tracking-tight text-[#1B1614] leading-[1.02]">
                Discover local shops.
                <span className="block text-[#B31E1E]">Buy from people you trust.</span>
              </h1>
              <p className="mt-4 text-[15px] md:text-base leading-relaxed text-[#5C4E48] max-w-xl">
                Every business here has verified reviews. Browse their catalog, message directly, and — for shops that go live — jump into their live-selling streams as it happens.
              </p>
            </div>
            <div className="flex flex-col gap-1.5 items-end min-w-[210px] hidden md:flex">
              <Link to="/marketplace" className="text-sm text-[#5C4E48] hover:text-[#B31E1E]" data-testid="shops-hero-marketplace">Or browse marketplace →</Link>
              <Link to="/auctions" className="text-sm text-[#5C4E48] hover:text-[#B31E1E]" data-testid="shops-hero-auctions">Or browse auctions →</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-8">
        <CurrentlyLiveStrip sessions={businessLive} />
        <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="shops-filters">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search shops"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="rounded-sm pl-9"
              data-testid="shops-search-input"
            />
          </div>
          <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
            <SelectTrigger className="w-52 rounded-sm" data-testid="shops-category-filter">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="rounded-sm w-36"
            data-testid="shops-city-filter"
          />
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="shops-sort-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="popular">Most popular</SelectItem>
              <SelectItem value="recent">Newest</SelectItem>
              <SelectItem value="top_rated">Top rated</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="shops-loading">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} className="rounded-md overflow-hidden">
                <div className="aspect-[16/9] bg-gray-100 animate-pulse" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-gray-100 rounded animate-pulse w-2/3" />
                  <div className="h-3 bg-gray-100 rounded animate-pulse w-3/4" />
                </div>
              </Card>
            ))}
          </div>
        ) : items.length === 0 ? (
          <Card className="rounded-md p-10 text-center" data-testid="shops-empty">
            <Store className="mx-auto mb-2 text-gray-400" size={32} />
            <p className="text-sm text-gray-600">
              No shops match your filters yet. Try widening the search or clear filters.
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="shops-grid">
            {displayedItems.map((b) => (
              <ShopCard
                key={b.id}
                shop={{
                  ...b,
                  is_live: liveHostIds.has(b.id),
                  live_viewer_count: liveViewerCountByHost.get(b.id) || 0,
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Currently-live strip — pinned to the top of `/shops` whenever any business
 * has a live-selling stream on air. Uses a red pulsing indicator + horizontal
 * scroll so it stays out of the way when quiet but grabs attention when
 * something's happening.
 */
function CurrentlyLiveStrip({ sessions }) {
  if (!sessions?.length) return null;
  return (
    <div className="mb-8" data-testid="shops-live-strip">
      <div className="flex items-center gap-2 mb-3">
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600" />
        </span>
        <p className="text-[11px] uppercase tracking-wider font-mono text-red-600 font-semibold">
          Live right now · {sessions.length} shop{sessions.length !== 1 ? "s" : ""}
        </p>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-[#EAE3DE]">
        {sessions.map((s) => (
          <Link
            key={s.id}
            to={`/marketplace/live/${s.id}`}
            className="group snap-start shrink-0 w-[280px] sm:w-[320px]"
            data-testid={`live-strip-card-${s.id}`}
          >
            <Card className="rounded-md overflow-hidden hover:border-red-500 transition-colors">
              <div className="relative aspect-video bg-gradient-to-br from-red-600 to-black overflow-hidden">
                {s.cover_image ? (
                  <img src={s.cover_image} alt={s.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white">
                    <TrendingUp size={44} />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-sm bg-red-600 text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                  </span>
                  Live
                </span>
                {s.viewer_count > 0 && (
                  <span className="absolute top-3 right-3 rounded-sm bg-black/60 backdrop-blur text-white text-[10px] font-mono px-2 py-0.5">
                    {s.viewer_count} watching
                  </span>
                )}
                <div className="absolute bottom-2 left-3 right-3 text-white">
                  <p className="text-[10px] uppercase tracking-wider font-mono text-[#F0C9C4] truncate">
                    {s.host_business_name || s.host_name || "Servrevu shop"}
                  </p>
                  <p className="font-semibold text-sm leading-tight line-clamp-2">{s.title}</p>
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------

function ShopCard({ shop }) {
  const url = `${window.location.origin}/shop/${shop.id}`;
  const [sharing, setSharing] = useState(false);

  const share = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSharing(true);
    const shareData = {
      title: shop.business_name || "Servrevu shop",
      text: `${shop.business_name || "This shop"} on Servrevu`,
      url,
    };
    try {
      if (navigator.share && !/desktop/i.test(navigator.userAgent)) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Shop link copied");
      }
    } catch {
      /* user cancelled */
    } finally { setSharing(false); }
  };

  const socialLinks = [
    { key: "twitter", label: "X / Twitter", href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shop.business_name || "")}` },
    { key: "facebook", label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
    { key: "sms", label: "SMS", href: `sms:?&body=${encodeURIComponent(`${shop.business_name || "Check out this shop"} — ${url}`)}` },
    { key: "email", label: "Email", href: `mailto:?subject=${encodeURIComponent(shop.business_name || "Servrevu shop")}&body=${encodeURIComponent(url)}` },
  ];

  const rating = typeof shop.rating_avg === "number" ? shop.rating_avg.toFixed(1) : null;
  const reviewCount = shop.review_count || 0;

  return (
    <div
      className={`group relative ${shop.is_live ? "ring-2 ring-red-500 ring-offset-2 rounded-md" : ""}`}
      data-testid={`shop-card-${shop.id}`}
    >
      <Link to={`/shop/${shop.id}`} className="block">
        <Card className="rounded-md overflow-hidden hover:border-[#B31E1E]/40 transition-colors h-full">
          <div className="relative aspect-[16/9] bg-gradient-to-br from-[#B31E1E] to-[#5C4E48] overflow-hidden">
            {shop.hero_image_url ? (
              <img src={shop.hero_image_url} alt={shop.business_name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            ) : shop.avatar_data_url ? (
              <img src={shop.avatar_data_url} alt={shop.business_name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white">
                <Store size={44} />
              </div>
            )}
            {shop.is_live && (
              <Badge className="absolute top-3 left-3 rounded-sm bg-red-600 text-white hover:bg-red-600 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                </span>
                Live{shop.live_viewer_count > 0 ? ` · ${shop.live_viewer_count}` : ""}
              </Badge>
            )}
            {rating && (
              <Badge className="absolute top-3 right-3 rounded-sm bg-white/95 backdrop-blur text-[#1B1614] hover:bg-white text-[10px] font-mono">
                <Star size={10} className="mr-1 fill-current text-amber-500" /> {rating}
              </Badge>
            )}
          </div>
          <div className="p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-[#1B1614] truncate">{shop.business_name}</h3>
                <p className="text-xs text-[#5C4E48] mt-1 flex items-center gap-1">
                  {shop.category_label || shop.category || "Local business"}
                </p>
              </div>
            </div>
            {(shop.business_city || shop.city) && (
              <p className="text-xs text-[#8B7B73] mt-2 inline-flex items-center gap-1">
                <MapPin size={10} />
                {shop.business_city || shop.city}
                {shop.business_state || shop.state ? `, ${shop.business_state || shop.state}` : ""}
              </p>
            )}
            {reviewCount > 0 && (
              <p className="text-[10px] font-mono text-[#8B7B73] mt-1">
                {reviewCount} review{reviewCount === 1 ? "" : "s"}
              </p>
            )}
          </div>
        </Card>
      </Link>

      {/* Floating share button — sits at the top-right of the whole card */}
      <div className="absolute bottom-14 right-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
        <details className="group/share">
          <summary
            className="list-none cursor-pointer flex items-center justify-center w-9 h-9 rounded-full bg-white border border-[#EAE3DE] shadow-sm hover:border-[#B31E1E] hover:text-[#B31E1E]"
            data-testid={`shop-share-${shop.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Share2 size={14} />
          </summary>
          <div
            className="absolute right-0 mt-1 min-w-[180px] bg-white border border-[#EAE3DE] rounded-md shadow-lg overflow-hidden text-[13px] z-20"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={share}
              disabled={sharing}
              type="button"
              className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-[#FAF5F2]"
              data-testid={`shop-share-native-${shop.id}`}
            >
              <LinkIcon size={12} /> Copy link
            </button>
            {socialLinks.map((s) => (
              <a
                key={s.key}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 hover:bg-[#FAF5F2] border-t border-[#EAE3DE]"
                data-testid={`shop-share-${s.key}-${shop.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                {s.label}
              </a>
            ))}
          </div>
        </details>
      </div>
    </div>
  );
}
